// Thin fetch wrapper for the JSON API. Sends cookies, attaches the CSRF token
// on mutations, and unwraps the shared `{ data } | { error }` envelope.

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

const BASE = '/api/v1';
const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

export interface ApiListMeta {
  page: number;
  pageSize: number;
  total: number;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<{ data: T; meta?: ApiListMeta }> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  // Don't set Content-Type for FormData — the browser must set it with the boundary.
  if (options.body && !(options.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  if (MUTATING.has(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : null;

  if (!res.ok) {
    const err = body?.error ?? { code: 'unknown', message: res.statusText };
    throw new ApiClientError(err.code, err.message, res.status, err.details);
  }
  return body as { data: T; meta?: ApiListMeta };
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => apiFetch<T>(path, { method: 'POST', body: form }),
};
